/**
 * AI Tools System for DapperGPT (AI SDK 6)
 * 
 * This module defines tools that can be used with AI SDK 6's tool calling feature.
 * Tools enable AI models to perform actions like web search, calculations,
 * web scraping, API calls, and more.
 * 
 * Tool categories:
 * - search: Web search and information retrieval
 * - compute: Mathematical and computational tools
 * - web: Web scraping and API tools
 * - utility: General purpose utilities
 */

import { z } from 'zod';
import { tool } from 'ai';
import { getModelConfig } from '../constant';

// =============================================================================
// PARAMETER SCHEMAS
// =============================================================================

const webSearchSchema = z.object({
    query: z.string().describe('The search query to find information on the web'),
    numResults: z.number().min(1).max(10).default(5).describe('Number of results to return'),
});

const webScraperSchema = z.object({
    url: z.string().url().describe('The URL to scrape content from'),
    selector: z.string().optional().describe('CSS selector to extract specific content'),
    extractType: z.enum(['text', 'html', 'markdown', 'links', 'images']).default('text'),
    maxLength: z.number().default(10000).describe('Maximum content length to return'),
    confirm: z.boolean().optional().describe('Set true only after user confirms this external fetch action'),
});

const apiCallSchema = z.object({
    url: z.string().url().describe('The API endpoint URL'),
    method: z.enum(['GET', 'POST', 'PUT', 'DELETE', 'PATCH']).default('GET'),
    headers: z.record(z.string(), z.string()).optional().describe('Request headers as key-value pairs'),
    body: z.string().optional().describe('Request body (JSON string for POST/PUT/PATCH)'),
    timeout: z.number().default(10000).describe('Request timeout in milliseconds'),
    confirm: z.boolean().optional().describe('Set true only after user confirms this external API request'),
});

const calculatorSchema = z.object({
    expression: z.string().describe('The mathematical expression to evaluate (e.g., "2 + 2", "15% of 200", "sqrt(16)")'),
});

const dateTimeSchema = z.object({
    timezone: z.string().optional().describe('Timezone (e.g., "America/New_York", "Europe/London")'),
    format: z.enum(['full', 'date', 'time', 'iso', 'unix']).default('full'),
    operation: z.enum(['now', 'dayOfWeek', 'daysUntil', 'daysSince', 'parse']).default('now'),
    targetDate: z.string().optional().describe('Target date for calculations (ISO format or natural language)'),
});

const codeExecuteSchema = z.object({
    code: z.string().describe('JavaScript code to execute safely'),
    timeout: z.number().default(5000).describe('Execution timeout in milliseconds'),
    confirm: z.boolean().optional().describe('Set true only after user confirms code execution'),
});

const memorySchema = z.object({
    operation: z.enum(['save', 'recall', 'list', 'delete']).describe('Memory operation to perform'),
    key: z.string().optional().describe('Key for the memory entry'),
    value: z.string().optional().describe('Value to store (for save operation)'),
});

// =============================================================================
// SEARCH TOOLS
// =============================================================================

/**
 * Web Search Tool - Uses Tavily/SerpAPI/DuckDuckGo for web search
 */
export const webSearchTool = tool({
    description: 'Search the web for current information. Use this when you need up-to-date info, news, or facts you don\'t know.',
    inputSchema: webSearchSchema,
    execute: async ({ query, numResults }) => {
        // This is a placeholder that should be configured with an actual search API
        // In production, integrate with Tavily, SerpAPI, or similar
        try {
            // Try DuckDuckGo Instant Answer API (free, no auth needed)
            const ddgUrl = `https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_html=1`;
            const response = await fetch(ddgUrl, {
                headers: { 'User-Agent': 'DapperGPT/1.0' }
            });

            if (response.ok) {
                const data = await response.json() as {
                    Abstract?: string;
                    AbstractSource?: string;
                    AbstractURL?: string;
                    RelatedTopics?: Array<{ Text?: string; FirstURL?: string }>;
                };

                const results: Array<{ title: string; snippet: string; url: string }> = [];

                if (data.Abstract) {
                    results.push({
                        title: data.AbstractSource || 'Summary',
                        snippet: data.Abstract,
                        url: data.AbstractURL || '',
                    });
                }

                if (data.RelatedTopics) {
                    for (const topic of data.RelatedTopics.slice(0, numResults - 1)) {
                        if (topic.Text && topic.FirstURL) {
                            results.push({
                                title: topic.Text.split(' - ')[0] || topic.Text.slice(0, 50),
                                snippet: topic.Text,
                                url: topic.FirstURL,
                            });
                        }
                    }
                }

                if (results.length > 0) {
                    return { success: true, query, results: results.slice(0, numResults) };
                }
            }

            return {
                success: false,
                query,
                message: 'No results found. For better results, configure Tavily or SerpAPI in settings.',
                results: [],
            };
        } catch (error) {
            return {
                success: false,
                query,
                error: (error as Error).message,
                results: [],
            };
        }
    },
});

/**
 * Web Scraper Tool - Fetch and extract content from any URL
 */
export const webScraperTool = tool({
    description: 'Scrape content from a webpage. Ask for user confirmation first, then call with confirm=true.',
    inputSchema: webScraperSchema,
    execute: async ({ url, extractType, maxLength, confirm }) => {
        if (!confirm) {
            return {
                success: false,
                requiresConfirmation: true,
                message: `Confirmation required before scraping ${url}`,
                suggestedArgs: { url, extractType, maxLength, confirm: true },
            };
        }

        const maxLen = maxLength ?? 10000;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);

            const response = await fetch(url, {
                signal: controller.signal,
                headers: {
                    'User-Agent': 'Mozilla/5.0 (compatible; DapperGPT/1.0)',
                    'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
                }
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                return { success: false, url, error: `HTTP ${response.status}: ${response.statusText}` };
            }

            const html = await response.text();
            let content: string;

            switch (extractType) {
                case 'html':
                    content = html.slice(0, maxLen);
                    break;

                case 'links': {
                    const linkMatches = html.match(/href="([^"]+)"/g) || [];
                    const links = linkMatches
                        .map(m => m.replace(/href="|"/g, ''))
                        .filter(l => l.startsWith('http'))
                        .slice(0, 50);
                    content = JSON.stringify(links, null, 2);
                    break;
                }

                case 'images': {
                    const imgMatches = html.match(/src="([^"]+\.(jpg|jpeg|png|gif|webp)[^"]*)"/gi) || [];
                    const images = imgMatches
                        .map(m => m.replace(/src="|"/g, ''))
                        .filter(l => l.startsWith('http'))
                        .slice(0, 20);
                    content = JSON.stringify(images, null, 2);
                    break;
                }

                case 'markdown':
                case 'text':
                default: {
                    // Clean HTML to readable text
                    content = html
                        // Remove script and style
                        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                        .replace(/<noscript[^>]*>[\s\S]*?<\/noscript>/gi, '')
                        // Remove nav, footer, header, aside
                        .replace(/<nav[^>]*>[\s\S]*?<\/nav>/gi, '')
                        .replace(/<footer[^>]*>[\s\S]*?<\/footer>/gi, '')
                        .replace(/<header[^>]*>[\s\S]*?<\/header>/gi, '')
                        .replace(/<aside[^>]*>[\s\S]*?<\/aside>/gi, '')
                        // Convert common elements
                        .replace(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi, '\n\n## $1\n\n')
                        .replace(/<p[^>]*>(.*?)<\/p>/gi, '\n$1\n')
                        .replace(/<br\s*\/?>/gi, '\n')
                        .replace(/<li[^>]*>(.*?)<\/li>/gi, '• $1\n')
                        .replace(/<a[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, '[$2]($1)')
                        .replace(/<strong[^>]*>(.*?)<\/strong>/gi, '**$1**')
                        .replace(/<em[^>]*>(.*?)<\/em>/gi, '*$1*')
                        .replace(/<code[^>]*>(.*?)<\/code>/gi, '`$1`')
                        // Remove remaining tags
                        .replace(/<[^>]+>/g, ' ')
                        // Clean up whitespace
                        .replace(/&nbsp;/g, ' ')
                        .replace(/&amp;/g, '&')
                        .replace(/&lt;/g, '<')
                        .replace(/&gt;/g, '>')
                        .replace(/&quot;/g, '"')
                        .replace(/\s+/g, ' ')
                        .replace(/\n\s*\n/g, '\n\n')
                        .trim()
                        .slice(0, maxLen);
                }
            }

            return {
                success: true,
                url,
                content,
                contentLength: content.length,
                contentType: response.headers.get('content-type'),
            };
        } catch (error) {
            const message = (error as Error).message;
            if (message.includes('abort')) {
                return { success: false, url, error: 'Request timed out' };
            }
            return { success: false, url, error: message || 'Failed to scrape URL' };
        }
    },
});

// =============================================================================
// WEB TOOLS
// =============================================================================

/**
 * API Call Tool - Make HTTP requests to any API
 */
export const apiCallTool = tool({
    description: 'Make HTTP requests to APIs. Ask for user confirmation first, then call with confirm=true.',
    inputSchema: apiCallSchema,
    execute: async ({ url, method, headers, body, timeout, confirm }) => {
        if (!confirm) {
            return {
                success: false,
                requiresConfirmation: true,
                message: `Confirmation required before ${method} request to ${url}`,
                suggestedArgs: { url, method, headers, body, timeout, confirm: true },
            };
        }

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const requestInit: RequestInit = {
                method,
                signal: controller.signal,
                headers: {
                    'User-Agent': 'DapperGPT/1.0',
                    'Accept': 'application/json',
                    ...headers,
                },
            };

            if (body && ['POST', 'PUT', 'PATCH'].includes(method)) {
                requestInit.body = body;
                if (!headers?.['Content-Type']) {
                    (requestInit.headers as Record<string, string>)['Content-Type'] = 'application/json';
                }
            }

            const response = await fetch(url, requestInit);
            clearTimeout(timeoutId);

            const responseHeaders: Record<string, string> = {};
            response.headers.forEach((value, key) => { responseHeaders[key] = value; });

            let data: unknown;
            const contentType = response.headers.get('content-type') || '';

            if (contentType.includes('application/json')) {
                data = await response.json();
            } else {
                data = await response.text();
            }

            return {
                success: response.ok,
                status: response.status,
                statusText: response.statusText,
                headers: responseHeaders,
                data,
            };
        } catch (error) {
            const message = (error as Error).message;
            return {
                success: false,
                error: message.includes('abort') ? 'Request timed out' : message,
            };
        }
    },
});

// =============================================================================
// COMPUTE TOOLS
// =============================================================================

/**
 * Calculator Tool - Perform mathematical calculations
 */
export const calculatorTool = tool({
    description: 'Perform mathematical calculations. Supports arithmetic, percentages, and math functions like sqrt, sin, cos, etc.',
    inputSchema: calculatorSchema,
    execute: async ({ expression }) => {
        try {
            let sanitized = expression
                .replace(/(\d+(?:\.\d+)?)\s*%\s*of\s*(\d+(?:\.\d+)?)/gi, '($1/100)*$2')
                .replace(/(\d+(?:\.\d+)?)\s*%/g, '($1/100)')
                .replace(/sqrt\s*\(/gi, 'Math.sqrt(')
                .replace(/abs\s*\(/gi, 'Math.abs(')
                .replace(/pow\s*\(/gi, 'Math.pow(')
                .replace(/sin\s*\(/gi, 'Math.sin(')
                .replace(/cos\s*\(/gi, 'Math.cos(')
                .replace(/tan\s*\(/gi, 'Math.tan(')
                .replace(/log\s*\(/gi, 'Math.log10(')
                .replace(/ln\s*\(/gi, 'Math.log(')
                .replace(/exp\s*\(/gi, 'Math.exp(')
                .replace(/ceil\s*\(/gi, 'Math.ceil(')
                .replace(/floor\s*\(/gi, 'Math.floor(')
                .replace(/round\s*\(/gi, 'Math.round(')
                .replace(/pi/gi, 'Math.PI')
                .replace(/\^/g, '**');

            if (!/^[0-9+\-*/().%\s,Math.PIeEsqrtabspowsincostalogxpceilfloorround]+$/.test(sanitized)) {
                return { success: false, expression, error: 'Invalid characters in expression' };
            }

            // eslint-disable-next-line no-new-func
            const result = Function('"use strict"; return (' + sanitized + ')')() as number;

            if (typeof result !== 'number' || !isFinite(result)) {
                return { success: false, expression, error: 'Result is not a valid number' };
            }

            return {
                success: true,
                expression,
                result: Number(result.toPrecision(12)),
                formatted: result.toLocaleString('en-US', { maximumFractionDigits: 10 }),
            };
        } catch (error) {
            return { success: false, expression, error: (error as Error).message };
        }
    },
});

/**
 * Date/Time Tool - Get current time and perform date calculations
 */
export const dateTimeTool = tool({
    description: 'Get current date/time or perform date calculations. Use this for time-sensitive questions.',
    inputSchema: dateTimeSchema,
    execute: async ({ timezone, format, operation, targetDate }) => {
        const now = new Date();

        if (operation === 'daysUntil' && targetDate) {
            const target = new Date(targetDate);
            const diffDays = Math.ceil((target.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
            return { success: true, operation, targetDate, daysUntil: diffDays };
        }

        if (operation === 'daysSince' && targetDate) {
            const target = new Date(targetDate);
            const diffDays = Math.floor((now.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
            return { success: true, operation, targetDate, daysSince: diffDays };
        }

        if (operation === 'dayOfWeek') {
            const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            return { success: true, dayOfWeek: days[now.getDay()], dayNumber: now.getDay() };
        }

        if (operation === 'parse' && targetDate) {
            const parsed = new Date(targetDate);
            return { success: true, input: targetDate, iso: parsed.toISOString(), timestamp: parsed.getTime() };
        }

        let options: Intl.DateTimeFormatOptions = {};

        switch (format) {
            case 'date': options = { dateStyle: 'full' }; break;
            case 'time': options = { timeStyle: 'medium' }; break;
            case 'iso': return { success: true, datetime: now.toISOString() };
            case 'unix': return { success: true, timestamp: Math.floor(now.getTime() / 1000) };
            default: options = { dateStyle: 'full', timeStyle: 'medium' };
        }

        if (timezone) options.timeZone = timezone;

        try {
            const formatter = new Intl.DateTimeFormat('en-US', options);
            return {
                success: true,
                datetime: formatter.format(now),
                timezone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
        } catch {
            return { success: false, error: 'Invalid timezone' };
        }
    },
});

// =============================================================================
// UTILITY TOOLS
// =============================================================================

/**
 * Code Execution Tool - Run JavaScript code safely
 */
export const codeExecuteTool = tool({
    description: 'Execute JavaScript code in a sandboxed environment. Ask for user confirmation first, then call with confirm=true.',
    inputSchema: codeExecuteSchema,
    execute: async ({ code, timeout, confirm }) => {
        if (!confirm) {
            return {
                success: false,
                requiresConfirmation: true,
                message: 'Confirmation required before executing code',
                suggestedArgs: { code, timeout, confirm: true },
            };
        }

        try {
            // Create a promise that resolves with the code result or rejects on timeout
            const result = await Promise.race([
                new Promise((resolve, reject) => {
                    try {
                        // Create a sandboxed function with limited scope
                        // eslint-disable-next-line no-new-func
                        const fn = new Function('console', `
                            "use strict";
                            const logs = [];
                            const mockConsole = {
                                log: (...args) => logs.push(['log', args]),
                                error: (...args) => logs.push(['error', args]),
                                warn: (...args) => logs.push(['warn', args]),
                            };
                            const result = (function() { ${code} })();
                            return { result, logs };
                        `);
                        resolve(fn(console));
                    } catch (e) {
                        reject(e);
                    }
                }),
                new Promise((_, reject) =>
                    setTimeout(() => reject(new Error('Execution timed out')), timeout)
                )
            ]) as { result: unknown; logs: Array<[string, unknown[]]> };

            return {
                success: true,
                result: result.result,
                logs: result.logs,
            };
        } catch (error) {
            return {
                success: false,
                error: (error as Error).message,
            };
        }
    },
});

/**
 * Memory Tool - Store and recall information
 */
const memoryStore = new Map<string, { value: string; timestamp: number }>();

export const memoryTool = tool({
    description: 'Save or recall information for later. Use this to remember things the user told you or important context.',
    inputSchema: memorySchema,
    execute: async ({ operation, key, value }) => {
        switch (operation) {
            case 'save':
                if (!key || !value) {
                    return { success: false, error: 'Key and value are required for save operation' };
                }
                memoryStore.set(key, { value, timestamp: Date.now() });
                return { success: true, operation: 'saved', key };

            case 'recall':
                if (!key) {
                    return { success: false, error: 'Key is required for recall operation' };
                }
                const entry = memoryStore.get(key);
                if (entry) {
                    return { success: true, key, value: entry.value, savedAt: new Date(entry.timestamp).toISOString() };
                }
                return { success: false, error: `No memory found for key: ${key}` };

            case 'list':
                const keys = Array.from(memoryStore.keys());
                return { success: true, keys, count: keys.length };

            case 'delete':
                if (!key) {
                    return { success: false, error: 'Key is required for delete operation' };
                }
                const deleted = memoryStore.delete(key);
                return { success: deleted, operation: 'deleted', key };

            default:
                return { success: false, error: 'Unknown operation' };
        }
    },
});

// =============================================================================
// TOOL REGISTRY
// =============================================================================

/**
 * All available tools for AI SDK 6
 */
export const availableTools = {
    webSearch: webSearchTool,
    webScraper: webScraperTool,
    apiCall: apiCallTool,
    calculator: calculatorTool,
    dateTime: dateTimeTool,
    codeExecute: codeExecuteTool,
    memory: memoryTool,
} as const;

export type ToolName = keyof typeof availableTools;

/**
 * Tool metadata for UI display
 */
export const toolMetadata: Record<ToolName, {
    name: string;
    description: string;
    icon: string;
    category: 'search' | 'compute' | 'web' | 'utility';
    enabled: boolean;
    requiresConfig?: boolean;
}> = {
    webSearch: {
        name: 'Web Search',
        description: 'Search the internet for current information',
        icon: '🔍',
        category: 'search',
        enabled: true,
    },
    webScraper: {
        name: 'Web Scraper',
        description: 'Scrape and extract content from any webpage',
        icon: '🌐',
        category: 'web',
        enabled: true,
    },
    apiCall: {
        name: 'API Caller',
        description: 'Make HTTP requests to APIs and web services',
        icon: '📡',
        category: 'web',
        enabled: true,
    },
    calculator: {
        name: 'Calculator',
        description: 'Perform mathematical calculations',
        icon: '🧮',
        category: 'compute',
        enabled: true,
    },
    dateTime: {
        name: 'Date & Time',
        description: 'Get current time and perform date calculations',
        icon: '📅',
        category: 'utility',
        enabled: true,
    },
    codeExecute: {
        name: 'Code Runner',
        description: 'Execute JavaScript code safely',
        icon: '💻',
        category: 'compute',
        enabled: true,
    },
    memory: {
        name: 'Memory',
        description: 'Remember and recall information',
        icon: '🧠',
        category: 'utility',
        enabled: true,
    },
};

/**
 * Create a tool set from tool names
 */
export const createToolSet = (toolNames: ToolName[]): Record<string, (typeof availableTools)[ToolName]> => {
    const tools: Record<string, (typeof availableTools)[ToolName]> = {};
    for (const name of toolNames) {
        if (availableTools[name]) {
            tools[name] = availableTools[name];
        }
    }
    return tools;
};

/**
 * Get all enabled tools
 */
export const getEnabledTools = (): Record<string, (typeof availableTools)[ToolName]> => {
    const tools: Record<string, (typeof availableTools)[ToolName]> = {};
    for (const [name, metadata] of Object.entries(toolMetadata)) {
        if (metadata.enabled) {
            tools[name] = availableTools[name as ToolName];
        }
    }
    return tools;
};

/**
 * Check if a model supports tool calling.
 *
 * Driven by the model registry's `function_calling` capability rather than
 * substring matching on the model ID. The previous prefix-list approach silently
 * returned false for any model whose name didn't happen to contain a known
 * fragment (e.g. 'claude-fable-5', 'gemini-3.6-flash', 'deepseek-v4-flash'),
 * which disabled tools for them with no error.
 *
 * Unknown model IDs fall back to `true`: a model we have no metadata for is
 * assumed modern, since every current frontier model supports tool calling.
 */
export const supportsToolCalling = (modelId: string): boolean => {
    const config = getModelConfig(modelId);
    if (!config) return true;
    return config.capabilities.includes('function_calling');
};
